import './styles.css';
import {defaultSelection, loadCatalog, selectedVariants} from './lib/catalog.js';
import {applyPrintifyBatchMetadata, capturePrintifyBatchMaster, createPrintifyApparelDrafts, createPrintifyDraftsFromMasters, createPublishIntent, executeStorefrontCatalog, generatePrintifyBatchMetadata, loadPrintifyApparelRoutes, loadPrintifyListingBatch, prepareStorefrontCatalog, publishPrintifyApparelDrafts, savePrintifyBatchMetadata, saveProductMapping, uploadAsset} from './lib/backend-api.js';
import {bindFulfillment} from './lib/fulfillment-controller.js';
import {canvasBlob, loadDesign, positionedPrintBlob, renderMockup} from './lib/mockup.js';
import {buildProductInput, createOrRecoverProduct, publishProduct, validateCompareAtPrice, validateRetailPrice} from './lib/shopify-products.js';
import {renderApparelDrafts, renderApparelMetadata, renderApparelRoutes, renderCatalogMasters, renderChoices, renderPreviewColors, renderShell, renderTemplateSelect, setStatus} from './lib/render-creator.js';

const root = document.querySelector('#app');
const PENDING_KEY = 'printew-pod-pending-publish-v1';
const APPAREL_BATCH_KEY = 'printew-listing-batch-v2';
const PENDING_MASTER_BATCH_KEY = 'printew-pending-master-batch-v1';
const state = {
  templates: [], template: null, colors: new Set(), sizes: new Set(),
  design: null, previewColor: 'WHITE',
  placement: {scale: 0.82, x: 0, y: 0}, pendingPublish: loadPending(),
  apparel: {routes: [], shops: [], masters: [], batch: loadApparelBatch(), pendingMasterBatch: loadPendingMasterBatch()},
};

start().catch((error) => {
  root.innerHTML = `<s-page heading="POD Studio"><s-banner tone="critical" heading="POD Studio could not start"><s-paragraph>${escapeHtml(error.message)}</s-paragraph></s-banner></s-page>`;
});

async function start() {
  state.templates = await loadCatalog();
  if (!state.templates.length) throw new Error('Production template catalog is empty.');
  state.template = state.templates[0];
  applyDefaults();
  renderShell(root);
  renderTemplateSelect(byId('template-select'), state.templates, state.template.key);
  syncControls();
  bindEvents();
  bindFulfillment(root);
  bindApparelBatch();
  await drawPreview();
  await loadApparelBatchResources();
  if (state.pendingPublish) {
    setStatus(byId('publish-status'), 'An unfinished Shopify product is ready to resume. Click Publish.', 'warning');
  }
}

function bindEvents() {
  root.addEventListener('click', handleClick);
  root.addEventListener('submit', handleSubmit);
  byId('template-select').addEventListener('change', handleTemplate);
  byId('design-file').addEventListener('change', handleDesign);
  byId('preview-color').addEventListener('change', (event) => {
    state.previewColor = event.target.value;
    drawPreview();
  });
  ['design-scale', 'design-x', 'design-y'].forEach((id) => {
    byId(id).addEventListener('input', handlePlacement);
  });
  byId('color-options').addEventListener('change', handleChoices);
  byId('size-options').addEventListener('change', handleChoices);
}

function handleSubmit(event) {
  if (event.target.id !== 'apparel-metadata-form') return;
  event.preventDefault();
  saveAndApplyApparelMetadata(event.target);
}

async function handleClick(event) {
  if (event.target.id === 'publish-button') return publish();
  if (event.target.id === 'create-apparel-drafts') return createApparelDrafts();
  if (event.target.id === 'create-drafts-from-masters') return createDraftsFromMasters();
  if (event.target.id === 'setup-storefront-catalog') return setupCatalogCollections();
  if (event.target.id === 'generate-apparel-metadata') return generateApparelMetadata();
  const masterButton = event.target.closest('[data-action="capture-master"]');
  if (masterButton) return captureBatchMaster(masterButton.dataset.productId);
  if (event.target.id === 'publish-selected-apparel') return publishSelectedApparelDrafts();
}

async function setupCatalogCollections() {
  const status = byId('catalog-setup-status');
  try {
    setApparelBusy(true);
    setStatus(status, 'Preparing an exact server-authorized catalog snapshot…');
    const {authorization} = await prepareStorefrontCatalog();
    setStatus(status, 'Applying the authorized catalog operation…');
    const result = await executeStorefrontCatalog(authorization.intentId);
    const collections = result.created.length ? `Created: ${result.created.join(', ')}.` : 'All catalog collections already existed.';
    const updated = result.updated.length ? ` Updated rules: ${result.updated.join(', ')}.` : '';
    const published = result.published.length ? ` Published ${result.published.length} collection(s) to Online Store.` : '';
    const products = result.tagged.length ? ` Tagged ${result.tagged.length} recognizable existing listing(s).` : '';
    const corrections = result.corrected.length ? ` Corrected product type tags on ${result.corrected.length} listing(s).` : '';
    const skipped = result.skipped.length ? ` Left ${result.skipped.length} unclassified listing(s) unchanged.` : '';
    setStatus(status, `${collections}${updated}${published}${products}${corrections}${skipped} Main menu updated.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

function bindApparelBatch() {
  byId('apparel-routes').addEventListener('change', syncApparelControls);
  byId('apparel-masters').addEventListener('change', syncApparelControls);
}

async function loadApparelBatchResources() {
  try {
    const {shops, routes, masters} = await loadPrintifyApparelRoutes();
    state.apparel.shops = shops;
    state.apparel.routes = routes;
    state.apparel.masters = masters;
    const select = byId('apparel-shop');
    select.innerHTML = shops.map((shop) => `<option value="${escapeHtml(shop.id)}">${escapeHtml(shop.title)}</option>`).join('');
    if (!shops.length) throw new Error('No connected Printify shop is available.');
    renderApparelRoutes(byId('apparel-routes'), routes);
    if (state.apparel.batch?.batchId) {
      const {batch} = await loadPrintifyListingBatch(state.apparel.batch.batchId);
      state.apparel.batch = batch;
      saveApparelBatch(batch);
    }
    renderApparelBatch();
    syncApparelControls();
  } catch (error) {
    setStatus(byId('apparel-status'), error.message, 'error');
    byId('apparel-routes').innerHTML = '<p class="empty-state compact">Printify routes are unavailable. Check the listing token and try again after deployment.</p>';
  }
}

function syncApparelControls() {
  const selected = root.querySelectorAll('input[name="apparel-route"]:checked').length;
  byId('create-apparel-drafts').disabled = selected === 0 || !state.apparel.shops.length;
  const selectedMasters = root.querySelectorAll('input[name="catalog-master"]:checked').length;
  byId('create-drafts-from-masters').disabled = selectedMasters === 0 || !state.apparel.shops.length;
}

async function createApparelDrafts() {
  const status = byId('apparel-status');
  const file = byId('apparel-design-file').files?.[0];
  const title = byId('apparel-title').value.trim();
  const selectedGroups = new Set([...root.querySelectorAll('input[name="apparel-route"]:checked')].map(({value}) => value));
  const routes = state.apparel.routes.filter((route) => selectedGroups.has(route.group) && route.available);
  try {
    if (!file) throw new Error('Upload one PNG design for the apparel batch.');
    if (file.type !== 'image/png') throw new Error('The apparel batch accepts a PNG design.');
    if (!title) throw new Error('Enter a base listing title.');
    if (!routes.length) throw new Error('Select at least one available apparel route.');
    setApparelBusy(true);
    setStatus(status, 'Uploading the original PNG and creating Printify drafts…');
    const artworkHash = await fileSha256(file);
    const {intentId} = await createPublishIntent();
    const {url: artworkUrl, key: artworkKey} = await uploadAsset(file, 'design', intentId);
    const result = await createPrintifyApparelDrafts({
      shopId: Number(byId('apparel-shop').value), designName: title, artworkUrl, artworkKey, artworkHash,
      routes: routes.map(({group, blueprintId, printProviderId, variantIds}) => ({group, blueprintId, printProviderId, variantIds})),
    });
    state.apparel.batch = result;
    saveApparelBatch(result);
    renderApparelBatch();
    setStatus(status, `${result.products.length} Printify drafts created. Review mockups, then generate metadata.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

async function createDraftsFromMasters() {
  const status = byId('apparel-status');
  const file = byId('apparel-design-file').files?.[0];
  const designName = byId('apparel-title').value.trim();
  const groupKeys = [...root.querySelectorAll('input[name="catalog-master"]:checked')].map(({value}) => value);
  try {
    if (!file) throw new Error('Upload one PNG design for the master batch.');
    if (file.type !== 'image/png') throw new Error('The master batch accepts a PNG design.');
    if (!designName) throw new Error('Enter a design name.');
    if (!groupKeys.length) throw new Error('Select at least one saved master template.');
    const shopId = Number(byId('apparel-shop').value);
    const batchId = pendingMasterBatchId({shopId, designName, groupKeys});
    setApparelBusy(true);
    setStatus(status, 'Uploading the original PNG and creating drafts from saved masters…');
    const artworkHash = await fileSha256(file);
    const {intentId} = await createPublishIntent();
    const {url: artworkUrl, key: artworkKey} = await uploadAsset(file, 'design', intentId);
    const result = await createPrintifyDraftsFromMasters({batchId, shopId, designName, artworkUrl, artworkKey, artworkHash, groupKeys});
    state.apparel.batch = result;
    saveApparelBatch(result);
    clearPendingMasterBatch();
    renderApparelBatch();
    setStatus(status, `${result.products.length} master-based drafts created. Review Printify mockups, then generate metadata.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

async function captureBatchMaster(productId) {
  const status = byId('apparel-status');
  const batch = state.apparel.batch;
  try {
    if (!batch?.batchId) throw new Error('Create a setup batch before saving a master template.');
    setApparelBusy(true);
    setStatus(status, 'Reading the reviewed Printify draft and saving its master snapshot…');
    const {master} = await capturePrintifyBatchMaster({batchId: batch.batchId, productId});
    state.apparel.masters = [...state.apparel.masters.filter(({group}) => group !== master.group), master]
      .sort((left, right) => listingGroupOrder(left.group) - listingGroupOrder(right.group));
    renderApparelBatch();
    syncApparelControls();
    setStatus(status, `${master.label} master v${master.version} saved.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

async function publishSelectedApparelDrafts() {
  const status = byId('apparel-status');
  const productIds = [...root.querySelectorAll('input[name="apparel-draft"]:checked')].map(({value}) => value);
  try {
    const batch = state.apparel.batch;
    if (!batch?.batchId) throw new Error('Create a catalog batch before publishing.');
    if (!batch.metadata?.ready) throw new Error('Generate metadata before publishing.');
    if (!productIds.length) throw new Error('Select at least one Printify draft to publish.');
    if (!window.confirm(`Publish ${productIds.length} selected Printify listing(s)?`)) return;
    setApparelBusy(true);
    setStatus(status, 'Publishing selected listings through the connected Printify sales channel…');
    await publishPrintifyApparelDrafts({batchId: batch.batchId, productIds});
    batch.products = batch.products.filter((draft) => !productIds.includes(draft.productId));
    saveApparelBatch(batch);
    renderApparelBatch();
    setStatus(status, `${productIds.length} selected listing(s) submitted to the Printify publish flow. Verify the Shopify channel status before advertising them.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

async function generateApparelMetadata() {
  const status = byId('apparel-status');
  const batch = state.apparel.batch;
  try {
    if (!batch?.batchId) throw new Error('Create a catalog batch before generating metadata.');
    let artworkKey = batch.artworkKey;
    if (!artworkKey) {
      const file = byId('apparel-design-file').files?.[0];
      if (!file || file.type !== 'image/png') throw new Error('This existing batch needs the original PNG selected once before metadata can be generated.');
      const artworkHash = await fileSha256(file);
      if (batch.artworkHash && batch.artworkHash !== artworkHash) throw new Error('Choose the original PNG used to create these drafts.');
      const {intentId} = await createPublishIntent();
      ({key: artworkKey} = await uploadAsset(file, 'design', intentId));
    }
    setApparelBusy(true);
    setStatus(status, 'Generating US-English metadata with GPT-5.6 Luna and applying it to every draft…');
    const result = await generatePrintifyBatchMetadata({batchId: batch.batchId, artworkKey});
    state.apparel.batch = result.batch;
    saveApparelBatch(result.batch);
    renderApparelBatch();
    setStatus(status, `AI metadata generated and applied to ${result.batch.products.length} draft(s). Review Printify mockups, then publish selected listings.`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

async function saveAndApplyApparelMetadata(form) {
  const status = byId('apparel-status');
  const batch = state.apparel.batch;
  try {
    if (!batch?.batchId) throw new Error('Create a catalog batch before saving metadata.');
    const fields = new FormData(form);
    const tags = String(fields.get('tags') || '').split(',').map((tag) => tag.trim()).filter(Boolean);
    setApparelBusy(true);
    setStatus(status, 'Saving reviewed metadata and applying it to every draft…');
    const saved = await savePrintifyBatchMetadata({
      batchId: batch.batchId, baseTitle: fields.get('baseTitle'), descriptionHtml: fields.get('descriptionHtml'), tags,
      seoTitle: fields.get('seoTitle'), seoDescription: fields.get('seoDescription'), handleSuggestion: fields.get('handleSuggestion'),
      altText: fields.get('altText'), reviewConfirmed: fields.get('reviewConfirmed') === 'on',
    });
    const result = await applyPrintifyBatchMetadata({batchId: batch.batchId, productIds: saved.batch.products.map(({productId}) => productId)});
    state.apparel.batch = result.batch;
    saveApparelBatch(result.batch);
    renderApparelBatch();
    setStatus(status, `Reviewed metadata saved and applied to ${result.batch.products.length} draft(s).`, 'success');
  } catch (error) {
    setStatus(status, error.message, 'error');
  } finally {
    setApparelBusy(false);
  }
}

function setApparelBusy(busy) {
  root.querySelectorAll('#apparel-panel input, #apparel-panel select, #apparel-panel button')
    .forEach((control) => { control.disabled = busy; });
}

function renderApparelBatch() {
  const batch = state.apparel.batch;
  renderCatalogMasters(byId('apparel-masters'), state.apparel.masters);
  renderApparelDrafts(byId('apparel-drafts'), batch?.products || [], batch?.metadata || null, state.apparel.masters);
  renderApparelMetadata(byId('apparel-metadata'), batch);
}

function loadApparelBatch() {
  try {
    const batch = JSON.parse(localStorage.getItem(APPAREL_BATCH_KEY));
    return batch?.batchId && Array.isArray(batch.products) ? batch : null;
  } catch { return null; }
}

function saveApparelBatch(batch) {
  try { localStorage.setItem(APPAREL_BATCH_KEY, JSON.stringify(batch)); } catch { /* Current session still holds review state. */ }
}

function pendingMasterBatchId({shopId, designName, groupKeys}) {
  const current = state.apparel.pendingMasterBatch;
  if (current && current.shopId === shopId && current.designName === designName && sameValues(current.groupKeys, groupKeys)) return current.batchId;
  const pending = {batchId: crypto.randomUUID(), shopId, designName, groupKeys: [...groupKeys].sort()};
  state.apparel.pendingMasterBatch = pending;
  try { localStorage.setItem(PENDING_MASTER_BATCH_KEY, JSON.stringify(pending)); } catch { /* Retry remains available in this session. */ }
  return pending.batchId;
}

function loadPendingMasterBatch() {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_MASTER_BATCH_KEY));
    return pending?.batchId && Number.isInteger(pending.shopId) && pending.designName && Array.isArray(pending.groupKeys) ? pending : null;
  } catch { return null; }
}

function clearPendingMasterBatch() {
  state.apparel.pendingMasterBatch = null;
  try { localStorage.removeItem(PENDING_MASTER_BATCH_KEY); } catch { /* No persisted retry state. */ }
}

function sameValues(left, right) { return [...left].sort().join('|') === [...right].sort().join('|'); }
function listingGroupOrder(group) { return ['tshirt', 'hoodie', 'sweatshirt', 'long-sleeve', 'tank-top', 'mug', 'sticker'].indexOf(group); }

async function fileSha256(file) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function handleTemplate(event) {
  state.template = state.templates.find(({key}) => key === event.target.value);
  applyDefaults();
  byId('product-price').value = Number(state.template.pricingRule?.floor || 24.95).toFixed(2);
  syncControls();
  drawPreview();
}

async function handleDesign(event) {
  const [file] = event.target.files;
  try {
    state.design = await loadDesign(file);
    if (!byId('product-title').value) byId('product-title').value = titleFromFilename(file.name);
    if (!byId('product-description').value) byId('product-description').value = `Original ${byId('product-title').value} artwork, made to order for your selected color and size.`;
    setStatus(byId('publish-status'), 'Artwork ready. Position it, then publish.', 'success');
    await drawPreview();
  } catch (error) {
    state.design = null;
    setStatus(byId('publish-status'), error.message, 'error');
  }
}

function handlePlacement() {
  state.placement = {
    scale: Number(byId('design-scale').value) / 100,
    x: Number(byId('design-x').value) / 100,
    y: Number(byId('design-y').value) / 100,
  };
  drawPreview();
}

function handleChoices(event) {
  const targetSet = event.target.name === 'color' ? state.colors : state.sizes;
  event.target.checked ? targetSet.add(event.target.value) : targetSet.delete(event.target.value);
  if (event.target.name === 'color' && !state.colors.has(state.previewColor)) {
    state.previewColor = [...state.colors][0] || state.template.colors[0];
  }
  syncControls();
  drawPreview();
}

function applyDefaults() {
  const selection = defaultSelection(state.template);
  state.colors = new Set(selection.colors);
  state.sizes = new Set(selection.sizes);
  state.previewColor = selection.colors.includes('WHITE') ? 'WHITE' : selection.colors[0];
}

function syncControls() {
  renderChoices(byId('color-options'), 'color', state.template.colors, state.colors);
  renderChoices(byId('size-options'), 'size', state.template.sizes, state.sizes);
  renderPreviewColors(byId('preview-color'), [...state.colors], state.previewColor);
  byId('preview-name').textContent = state.template.name;
  byId('product-price').min = Number(state.template.pricingRule?.floor || 1).toFixed(2);
  if (!byId('product-price').value) {
    byId('product-price').value = Number(state.template.pricingRule?.floor || 24.95).toFixed(2);
  }
  const count = selectedVariants(state.template, state.colors, state.sizes).length;
  byId('variant-summary').textContent = `${count} production variants selected`;
}

async function drawPreview() {
  await renderMockup(byId('mockup-canvas'), {
    template: state.template,
    color: state.previewColor,
    design: state.design,
    placement: state.placement,
  });
}

async function publish() {
  const status = byId('publish-status');
  try {
    setCreatorBusy(true);
    if (state.pendingPublish) return await finalizePending(state.pendingPublish);
    const snapshot = creatorSnapshot();
    validateSnapshot(snapshot);
    const {intentId} = await createPublishIntent();
    const handle = `printew-${intentId}`;
    setStatus(status, 'Uploading artwork and mockups…');
    const printBlob = await positionedPrintBlob(snapshot);
    const printFile = new File([printBlob], 'front-print.png', {type: 'image/png'});
    const {url: printUrl} = await uploadAsset(printFile, 'design', intentId);
    const mockupUrls = await uploadMockups(snapshot, intentId);
    const input = buildProductInput({
      title: snapshot.title, template: snapshot.template, variants: snapshot.variants,
      price: snapshot.price, compareAtPrice: snapshot.compareAtPrice, mockupUrls, handle, intentId,
    });
    setPending({
      stage: 'creating', handle, productInput: input,
      mappingContext: {
        intentId, title: snapshot.title, templateKey: snapshot.template.key,
        variants: snapshot.variants, printUrl, mockupUrls,
      },
    });
    await finalizePending(state.pendingPublish);
  } catch (error) {
    const productId = state.pendingPublish?.productId?.split('/').pop();
    const retry = state.pendingPublish
      ? productId
        ? ` Product ${productId} was created; click Publish again to resume.`
        : ' Click Publish again to recover the in-progress product.'
      : '';
    setStatus(status, `${error.message}${retry}`, 'error');
  } finally {
    setCreatorBusy(false);
  }
}

async function uploadMockups(snapshot, intentId) {
  const urls = {};
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 1200;
  for (const color of snapshot.colors) {
    await renderMockup(canvas, {...snapshot, color});
    const blob = await canvasBlob(canvas);
    const file = new File([blob], `mockup-${color.toLowerCase()}.jpg`, {type: 'image/jpeg'});
    urls[color] = (await uploadAsset(file, 'mockup', intentId)).url;
  }
  return urls;
}

function buildMapping(product, context) {
  const shopifyBySku = new Map(product.variants.nodes.map((item) => [item.inventoryItem.sku, item.id]));
  const missing = context.variants.find((variant) => !shopifyBySku.has(variant.sku));
  if (missing) throw new Error(`Shopify did not return variant ${missing.sku}.`);
  return {
    productId: product.id, intentId: context.intentId,
    title: context.title, templateKey: context.templateKey, designUrl: context.printUrl,
    variants: context.variants.map((variant) => ({
      shopifyVariantId: shopifyBySku.get(variant.sku), mangoSku: variant.sku,
      mangoVariationId: variant.variationId, previewUrl: context.mockupUrls[variant.color],
      printUrl: context.printUrl,
    })),
  };
}

async function finalizePending(pending) {
  if (pending.stage === 'creating') {
    setStatus(byId('publish-status'), 'Creating or recovering Shopify product…');
    const product = await createOrRecoverProduct(pending.productInput, pending.handle);
    pending.productId = product.id;
    pending.product = product;
    pending.stage = 'building-mapping';
    setPending(pending);
  }
  if (pending.stage === 'building-mapping') {
    pending.mapping = buildMapping(pending.product, pending.mappingContext);
    pending.stage = 'mapping';
    setPending(pending);
  }
  if (pending.stage === 'mapping') {
    setStatus(byId('publish-status'), 'Saving production references for manual routing…');
    await saveProductMapping(pending.mapping);
    pending.stage = 'publishing';
    setPending(pending);
  }
  setStatus(byId('publish-status'), 'Publishing to Online Store and Shop…');
  await publishProduct(pending.productId, pending.handle);
  setPending(null);
  const id = pending.productId.split('/').pop();
  setStatus(byId('publish-status'), `Published for manual fulfillment. Shopify product ${id}.`, 'success');
  window.shopify?.toast?.show?.('POD product published');
}

function creatorSnapshot() {
  return {
    title: byId('product-title').value.trim(),
    description: byId('product-description').value.trim(),
    price: byId('product-price').value,
    compareAtPrice: byId('product-compare-at-price').value,
    template: state.template,
    colors: [...state.colors],
    sizes: [...state.sizes],
    variants: selectedVariants(state.template, state.colors, state.sizes).map((variant) => ({...variant})),
    design: state.design,
    placement: {...state.placement},
  };
}

function validateSnapshot(snapshot) {
  if (!snapshot.design) throw new Error('Upload a PNG design first.');
  if (!snapshot.title) throw new Error('Enter a product title.');
  if (!snapshot.description) throw new Error('Enter a product description.');
  if (!snapshot.variants.length) throw new Error('Select at least one available variant.');
  validateRetailPrice(snapshot.price, snapshot.template);
  validateCompareAtPrice(snapshot.compareAtPrice, Number(snapshot.price));
}

function setCreatorBusy(busy) {
  root.querySelectorAll('#creator-panel input, #creator-panel select, #creator-panel button')
    .forEach((control) => { control.disabled = busy; });
}

function setPending(pending) {
  state.pendingPublish = pending;
  try {
    if (pending) localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_KEY);
  } catch { /* Recovery remains available for the current page session. */ }
}

function loadPending() {
  try {
    const value = JSON.parse(localStorage.getItem(PENDING_KEY));
    const stages = ['creating', 'building-mapping', 'mapping', 'publishing'];
    return stages.includes(value?.stage) ? value : null;
  } catch {
    try { localStorage.removeItem(PENDING_KEY); } catch { /* Storage is unavailable. */ }
    return null;
  }
}

function byId(id) { return document.getElementById(id); }
function titleFromFilename(name) { return name.replace(/\.png$/i, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()); }
function escapeHtml(value) { return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;'); }
